/* Keep floating app controls clear of the phone navigation without changing consent or chat state. */
(() => {
  if (window.schucoMobileWidgetsBound) return;
  window.schucoMobileWidgetsBound = true;

  const root = document.documentElement;
  const mobile = window.matchMedia('(max-width: 767px)');
  let dock;
  let actionBars = [];
  let dockFrame;
  let chat;
  const consentHosts = new Map();
  const consentStyleId = 'schuco-mobile-widget-position';

  // Pandectes v5 uses an open shadow root. Only its reopen button is styled;
  // the notice, preferences, focus handling and consent decisions stay with the app.
  const consentCSS = `
    @media screen and (max-width: 767px) {
      button.widget.bottom {
        bottom: var(--schuco-mobile-widget-bottom, 16px) !important;
      }
      button.widget {
        visibility: var(--schuco-mobile-utility-visibility, visible) !important;
        pointer-events: var(--schuco-mobile-utility-events, auto) !important;
      }
    }
  `;

  const measureDock = () => {
    dockFrame = null;
    // Reserve its space even while it is translated off screen, so the buttons
    // cannot overlap it during header, footer or keyboard-focus transitions.
    const height = mobile.matches && dock?.isConnected ? dock.getBoundingClientRect().height : 0;
    if (height > 0) {
      const value = `${Math.ceil(height)}px`;
      if (root.style.getPropertyValue('--schuco-mobile-dock-height') !== value) {
        root.style.setProperty('--schuco-mobile-dock-height', value);
      }
    }
    if (mobile.matches) {
      const actionHeight = Math.max(0, ...actionBars.filter((bar) => bar.isConnected).map((bar) => {
        const bottom = Math.max(0, parseFloat(getComputedStyle(bar).bottom) || 0);
        return bar.getBoundingClientRect().height + bottom;
      }));
      const value = `${Math.ceil(actionHeight)}px`;
      if (root.style.getPropertyValue('--schuco-mobile-action-height') !== value) {
        root.style.setProperty('--schuco-mobile-action-height', value);
      }
    }
  };

  const scheduleDock = () => {
    if (dockFrame == null) dockFrame = requestAnimationFrame(measureDock);
  };
  const dockObserver = 'ResizeObserver' in window ? new ResizeObserver(scheduleDock) : null;

  const readConsentState = () => {
    let open = false;
    consentHosts.forEach((observer, host) => {
      if (!host.isConnected) {
        observer.disconnect();
        consentHosts.delete(host);
      }
      else if (host.shadowRoot?.querySelector('.dialog[open], .dialog[data-open="true"]')) open = true;
    });
    root.toggleAttribute('data-schuco-consent-open', open);
  };

  const bindConsent = (host) => {
    const shadow = host.shadowRoot;
    if (!shadow) return;
    if (!consentHosts.has(host)) {
      const observer = new MutationObserver(readConsentState);
      observer.observe(shadow, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'data-open'] });
      consentHosts.set(host, observer);
    }
    if (!shadow.getElementById(consentStyleId)) {
      const style = document.createElement('style');
      style.id = consentStyleId;
      style.textContent = consentCSS;
      shadow.appendChild(style);
    }
    readConsentState();
  };

  const readChatState = (state) => {
    const visibility = state?.visibility;
    if (['minimized', 'maximized', 'hidden'].includes(visibility)) {
      root.setAttribute('data-schuco-chat-visibility', visibility);
    }
  };

  const bindChat = () => {
    const widget = window.LiveChatWidget;
    if (!widget?.on || widget === chat) return;
    chat = widget;
    document.removeEventListener('load', bindChat, true);
    // These listeners do not initialise the widget or change its visibility.
    widget.on('ready', (data) => readChatState(data?.state));
    widget.on('visibility_changed', readChatState);
    try { readChatState(widget.get('state')); } catch (error) { /* Still in the app's loading queue. */ }
  };

  const discover = () => {
    const nextDock = document.querySelector('.mobile-dock');
    const nextBars = [...document.querySelectorAll('.product-sticky-form, .facet-sticky')];
    if (nextDock !== dock || nextBars.length !== actionBars.length || nextBars.some((bar, index) => bar !== actionBars[index])) {
      dockObserver?.disconnect();
      dock = nextDock;
      actionBars = nextBars;
      root.style.removeProperty('--schuco-mobile-dock-height');
      if (dock) {
        dockObserver?.observe(dock);
      }
      actionBars.forEach((bar) => dockObserver?.observe(bar));
      scheduleDock();
    }
    document.querySelectorAll('pandectes-cmp').forEach(bindConsent);
    readConsentState();
    bindChat();
  };

  // App embeds and theme editor sections can arrive after this deferred script.
  // Ignore unrelated mutations; there is no scroll polling or page-wide attribute observer.
  const inserted = new MutationObserver((records) => {
    const relevant = records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
      node.nodeType === 1 && (node.matches('pandectes-cmp, .mobile-dock, .product-sticky-form, .facet-sticky, #chat-widget-container') ||
        node.querySelector('pandectes-cmp, .mobile-dock, .product-sticky-form, .facet-sticky, #chat-widget-container'))
    ));
    if (relevant) discover();
  });
  inserted.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('load', bindChat, true);
  window.addEventListener('resize', scheduleDock, { passive: true });
  mobile.addEventListener('change', scheduleDock);
  window.addEventListener('PandectesInitialized', discover);
  window.addEventListener('PandectesDialogOpened', () => root.setAttribute('data-schuco-consent-open', ''));
  window.addEventListener('PandectesDialogClosed', () => requestAnimationFrame(readConsentState));
  window.addEventListener('pageshow', discover);
  if (window.customElements) customElements.whenDefined('pandectes-cmp').then(discover);
  discover();
})();
